import {
    computed,
    PromiseResourceOptions,
    resource,
    ResourceLoader,
    ResourceParamsContext,
    ResourceRef,
    signal,
    Signal,
} from "@angular/core";
import { S25Util } from "./s25-util";

export type S25Resource<T> = {
    /**
     * The underlying Angular resource
     */
    resource: ResourceRef<T | undefined>;
    /**
     * Equivalent to `resource.hasValue() ? resource.value() : undefined`
     */
    value: Signal<T | undefined>;
    /**
     * Holds the last valid value returned by the loader.
     */
    lastValue: Signal<T | undefined>;
    /** True while the loader promise is in flight. */
    isLoading: Signal<boolean>;
    /** Rejection from the last load, if any. */
    error: Signal<unknown>;
    /**
     * `ResourceRef.reload()` only returns a boolean and does not wait for HTTP.
     * During `reloading`, `value()` still reflects the previous fetch — await this after reload before reading `value`.
     */
    reloadAndAwait(): Promise<T | undefined>;
    /**
     * Wait for an in-flight load to complete without triggering a new reload.
     * Use this when the resource is auto-triggered by reactive conditions and you just need to wait for the result.
     */
    awaitLoad(): Promise<T | undefined>;
};

export type S25ResourceOptions<T, TParams> = PromiseResourceOptions<T, TParams> & {
    /**
     * If this function evaluates to truthy, the params function will be evaluated.
     * Otherwise, no params will be generated, ergo no requests will be sent. This
     * function will be run in a computed context, so it will respond to signal updates.
     */
    if?: () => boolean | undefined;
    /**
     * When true, the loader does not run until a wrapper API is read (`value`,
     * `lastValue`, `isLoading`, `error`) or `reloadAndAwait` / `awaitLoad` is called.
     * Reading `.resource` directly does not start the load.
     *
     * First reads schedule the load on a microtask so `value()` can be used inside
     * `computed` without writing a signal in that computed.
     */
    lazy?: boolean;
};

/**
 * Wrapper for the `Promise` version of Angular's `resource`. Some of the fun features:
 * - Forces the loader function to reject promises with `Error`s
 * - Exposes a non-throwing `value` signal
 * - Exposes the "last value", i.e. the last successfully loaded value
 * - Accepts an `if` option to conditionally load the resource
 * - Accepts a `lazy` option to load only after a wrapper API is read
 */
export function s25Resource<T, TParams>(
    options: S25ResourceOptions<T, TParams> | ResourceLoader<T, TParams>,
): S25Resource<T> {
    if (typeof options === "function") {
        options = { loader: options };
    }

    const isLazy = !!options.lazy;
    const requested = signal(!isLazy);
    const ifSignal = typeof options.if === "function" ? computed(options.if) : undefined;

    const ensureRequested = (): void => {
        const isRequested = requested();
        if (!isRequested) queueMicrotask(() => requested.set(true));
    };

    const params =
        isLazy || ifSignal
            ? (ctx: ResourceParamsContext) => {
                  if (isLazy && !requested()) return undefined as TParams;
                  if (ifSignal && !ifSignal()) return undefined as TParams;
                  if (options.params) return options.params(ctx);
                  // Cannot be undefined: that would keep the resource idle.
                  return true as TParams;
              }
            : options.params;

    // TODO: consider handling `resource.set()` calls to update this as well
    const lastSuccessful = signal<T | undefined>(options.defaultValue);
    const loader: ResourceLoader<T, TParams> = (loadEvent) => {
        const promise = Promise.resolve(options.loader(loadEvent));
        promise
            .then((value) => {
                if (loadEvent.abortSignal.aborted) return;
                lastSuccessful.set(value);
            })
            .catch(() => {});
        return promise;
    };

    // Create resource
    const wrappedOptions = { ...options, params, loader };
    const ngResource = resource<T, TParams>({
        ...wrappedOptions,
        loader: enforceErrorsOnLoader<T, TParams>(wrappedOptions),
    });
    const RESOURCE_RELOAD_AWAIT_TIMEOUT_MS = 120_000;

    const ifAllowsLoad = (): boolean => !ifSignal || !!ifSignal();
    const isWaitingToStart = (): boolean =>
        isLazy && requested() && ifAllowsLoad() && ngResource.status() === "idle" && !ngResource.hasValue();

    const waitForLoad = async (caller: "reloadAndAwait" | "awaitLoad"): Promise<T | undefined> => {
        const deadlineMs = performance.now() + RESOURCE_RELOAD_AWAIT_TIMEOUT_MS;
        while (ngResource.isLoading() || isWaitingToStart()) {
            if (performance.now() > deadlineMs) {
                throw new Error(`s25Resource.${caller}: timed out waiting for resource load`);
            }
            // Macrotask yield — a tight queueMicrotask loop can starve fetch completion and freeze the tab.
            await new Promise<void>((resolve) => setTimeout(resolve, 100));
        }
        return ngResource.hasValue() ? ngResource.value() : undefined;
    };

    const reloadAndAwait = async () => {
        requested.set(true);
        const status = ngResource.status();
        if (status !== "loading" && (status !== "idle" || !ngResource.hasValue())) {
            ngResource.reload();
        }
        return waitForLoad("reloadAndAwait");
    };

    const awaitLoad = async () => {
        requested.set(true);
        return waitForLoad("awaitLoad");
    };

    return {
        resource: ngResource,
        value: computed<T | undefined>(() => {
            ensureRequested();
            return ngResource.hasValue() ? ngResource.value() : undefined;
        }),
        lastValue: computed(() => {
            ensureRequested();
            return lastSuccessful();
        }),
        isLoading: computed(() => {
            ensureRequested();
            const status = ngResource.status();
            return status === "loading" || status === "reloading";
        }),
        error: computed(() => {
            ensureRequested();
            return ngResource.error();
        }),
        reloadAndAwait,
        awaitLoad,
    };
}

/**
 * Angular expects that promises are rejected with Error instances, rather than strings or objects.
 * If the loader rejects with a non-error, angular will spit out some ugly error messaging,
 * so let's handle it explicitly.
 * thrown error object will be available in error.cause
 */
function enforceErrorsOnLoader<T, R>(options: PromiseResourceOptions<T, R>) {
    return (args: any) => {
        try {
            const promiseLike = options.loader(args);
            if ("catch" in promiseLike && typeof promiseLike.catch === "function") {
                return promiseLike.catch((e: any) => {
                    if (typeof e === "string") {
                        e = new Error(e);
                    } else if (!(e instanceof Error)) {
                        e = new Error(e?.message || S25Util.errorText(e) || "Unknown error", { cause: e });
                    }
                    if (!window?.unitTesting) {
                        console.error(...(e.cause ? [e.cause, "Caused: ", e] : [e]));
                    }
                    throw e;
                });
            } else {
                return promiseLike;
            }
        } catch (e) {
            console.error(e);
            return Promise.reject(e);
        }
    };
}

export namespace s25Resource {
    export function combine(...resources: S25Resource<any>[]) {
        return {
            isLoading: computed(() => resources.some((resource) => resource.isLoading())),
            hasError: computed(() => resources.some((resource) => resource.error())),
            errors: computed(() =>
                resources.filter((resource) => resource.error()).map((resource) => resource.error()),
            ),
            hasValue: computed(() => resources.every((resource) => resource.resource.hasValue())),
        };
    }
}
